/**
 * Tracer shader.
 *
 * A tracer is a segment, not a sprite: the head advances from the muzzle at a
 * plausible muzzle velocity and drags a fixed-length tail behind it. The quad is
 * built in view space from the two endpoints and widened along
 * `cross(segment, eyeVector)`, which stays stable when the round is fired straight
 * at or away from the camera — the case where a naive screen-space perpendicular
 * degenerates and the tracer flickers.
 *
 * The fragment is a bright near-white core inside a dimmer coloured halo. The core
 * is what keeps it legible against a blown-out sky; the halo is what keeps it
 * visible in shadow.
 */

export const tracerVert = /* glsl */ `
attribute vec3 tOrigin;
attribute vec3 tDir;
attribute vec4 tParams;   // speed, birth, total distance, width
attribute vec4 tCol;      // rgb, tail length

uniform float uTime;
uniform float uPxScale;    // world units per pixel at unit depth
uniform float uMinPixels;  // never let a tracer thin below this on screen

varying vec2 vUv;
varying vec3 vColor;
varying float vFade;

void main() {
  float t = uTime - tParams.y;
  float total = tParams.z;
  float tail = tCol.w;
  float travelled = tParams.x * t;

  if (t < 0.0 || total <= 0.0 || travelled - tail > total) {
    gl_Position = vec4(0.0, 0.0, 2.0, 1.0);
    return;
  }

  float head = min(travelled, total);
  float back = clamp(travelled - tail, 0.0, total);

  vec4 a = modelViewMatrix * vec4(tOrigin + tDir * back, 1.0);
  vec4 b = modelViewMatrix * vec4(tOrigin + tDir * head, 1.0);

  vec4 p = mix(a, b, uv.y);
  vec3 seg = b.xyz - a.xyz;
  vec3 toEye = -p.xyz;
  vec3 side = cross(seg, toEye);
  float sl = length(side);
  side = sl > 1e-5 ? side / sl : vec3(1.0, 0.0, 0.0);

  // Taper: thin at the tail, full width at the head. A world-space width alone
  // would shrink below a pixel at range and the round would simply vanish, so the
  // width is clamped to a minimum screen size.
  float w = tParams.w * mix(0.35, 1.0, uv.y);
  w = max(w, uMinPixels * max(-p.z, 0.01) * uPxScale);
  p.xyz += side * (uv.x - 0.5) * w;

  gl_Position = projectionMatrix * p;
  vUv = uv;
  vColor = tCol.rgb;

  // Dim as the round leaves the barrel behind and once it is nearly spent.
  vFade = 1.0 - smoothstep(0.75, 1.0, head / max(total, 0.001)) * 0.65;
}
`;

export const tracerFrag = /* glsl */ `
varying vec2 vUv;
varying vec3 vColor;
varying float vFade;

void main() {
  float r = abs(vUv.x - 0.5) * 2.0;
  float core = pow(max(0.0, 1.0 - r / 0.30), 2.0);
  float halo = pow(max(0.0, 1.0 - r), 3.5);
  float along = pow(vUv.y, 1.6);

  vec3 c = vColor * halo * 0.85 + vec3(1.0, 0.94, 0.82) * core * 2.6;
  gl_FragColor = vec4(c * along * vFade, 1.0);
}
`;
